import React from 'react'
// @src imports
import connect from 'src/redux/connect'
import { Transition } from 'src/chrome'
import { PricingRoute } from 'src/marketing/plans/routes/PricingRoute'
import {
  GetMarketingParagraphs,
  GetMarketingParagraphsData,
  GetPlans,
  GetPlansData,
} from 'src/graphql'
// relative imports
import { PlansDescription } from '../types'
import {
  AccountStorage,
  AutomatedSendsGrid,
  BasicPlan,
  PlanAddons,
  PlanContentsGrid,
  Plans,
  RoundedFooter,
  WaysToSend,
} from '../components'

import { openApp } from 'src/helpers/appHelpers'
import { performOperation } from 'src/app/api'
import { PromptingsPackageUpsale } from 'src/chrome/PromptingsPackageUpsale/PromptingsPackageUpsale'
import { Button, Flex, Image, Separator } from '@sendoutcards/quantum-design-ui'
import PromoItem from 'src/marketing/components/Promotion/PromoItem'

const discountDisclaimer: string[] = [
  '* Discounts apply to most gifts. Excludes: books, apparel, and business-building tools.',
  '** A Heartfelt Prompting Card has an immediate send date with only one recipient attached.',
  '*** A System Card has a future send date or more than one recipient.',
]

type RoutesActions = {
  openCampaignStore?: () => void
}

type PricingProps = {
  route?: PricingRoute
  screenWidth?: number
  isMobile?: boolean
  sponsorId?: string
} & RoutesActions

type State = {
  plansDescriptions: PlansDescription[]
  plansDescriptionsStatus: 'init' | 'loading' | 'loaded' | 'error'
  marketingParagraphs?: GetMarketingParagraphsData
  marketingParagraphsStatus: 'init' | 'loading' | 'loaded' | 'error'
  planAddons: [PlansDescription[], PlansDescription[]]
}

const initialState: State = {
  plansDescriptions: [],
  plansDescriptionsStatus: 'init',
  marketingParagraphs: undefined,
  marketingParagraphsStatus: 'init',
  planAddons: [[], []],
}

class Pricing extends React.Component<PricingProps, State> {
  state: State = {
    ...initialState,
  }

  showPlanUpgrade = (planId: string) => {
    // need to modify promo query param to be plan id
    openApp('', { join: '', promo: planId, planSelected: '' })
  }

  showPlanAddonBuy = (planAddonId: string) => {
    openApp('', { join: '', promo: planAddonId, planSelected: '' })
  }

  componentWillUnmount() {
    window.localStorage.removeItem('join-page')
  }

  componentDidMount() {
    if (this.state.plansDescriptionsStatus === 'init') {
      this.setState({ plansDescriptionsStatus: 'loading' })
      performOperation<GetPlansData>(GetPlans(), result => {
        if (result.error || !result.value) {
          this.setState({
            plansDescriptions: [],
            plansDescriptionsStatus: 'error',
            planAddons: [[], []],
          })
        } else {
          const [addons, plans] = result.value.plans.reduce(
            (acc: [PlansDescription[], PlansDescription[]], plan) => {
              const [addon, plans] = acc
              const planDescription = PlansDescription.fromPlanFragment(plan)

              if (!plan.description) {
                return [addon, plans]
              }
              if (plan.isAddon) {
                return [[planDescription, ...addon], plans]
              } else {
                return [addon, [...plans, planDescription]]
              }
            },
            [[], []],
          )

          const [promotionalAddons, baseAddons] = addons.reduce(
            (acc: [PlansDescription[], PlansDescription[]], option) => {
              const [promotional, base] = acc
              if (option.description && option.description.isPromotional) {
                return [[...promotional, option], base]
              } else {
                return [promotional, [...base, option]]
              }
            },
            [[], []],
          )
          this.setState({
            // tslint:disable-next-line:no-array-mutation
            plansDescriptions: plans.sort(
              (a, b) =>
                (a.description?.order ?? 10_000) -
                (b.description?.order ?? 10_000),
            ),
            planAddons: [promotionalAddons, baseAddons],
            plansDescriptionsStatus: 'loaded',
          })
        }
      })
    }
    if (this.state.marketingParagraphsStatus === 'init') {
      this.setState({ marketingParagraphsStatus: 'loading' })
      performOperation(GetMarketingParagraphs(), result => {
        if (result.error || !result.value) {
          this.setState({
            marketingParagraphs: undefined,
            marketingParagraphsStatus: 'error',
          })
        } else {
          this.setState({
            marketingParagraphs: result.value,
            marketingParagraphsStatus: 'loaded',
          })
        }
      })
    }
  }

  render() {
    if (
      this.state.plansDescriptionsStatus === 'init' ||
      this.state.plansDescriptionsStatus === 'loading' ||
      this.state.marketingParagraphsStatus === 'init' ||
      this.state.marketingParagraphsStatus === 'loading'
    ) {
      return <Transition message="Loading plans..." />
    }

    const basicPlan = this.state.plansDescriptions.find(p => p.id === '1')
    const [promotionalAddons, baseAddons] = this.state.planAddons
    return (
      <Flex
        flexDirection="column"
        width="100%"
        justifyContent="center"
        alignItems="center"
      >
        <Plans
          plansDescriptions={this.state.plansDescriptions}
          pricingTitle={
            this.state.marketingParagraphs?.pricingPageTitle.content
          }
          pricingSubtitle={
            this.state.marketingParagraphs?.pricingPageSubtitle.content
          }
          onSelect={this.showPlanUpgrade}
        />

        {promotionalAddons.length > 0 && (
          <Flex width="80%" flexDirection="column" outset={{ vertical: 'x5' }}>
            <Separator caption="Or" orientation="horizontal" />
            {promotionalAddons.map(addon => {
              const handleAction = () =>
                window.open(
                  `https://www.sendoutcards.com/app/promo?promo=3&sponsorId=${this.props.sponsorId}`,
                )
              return (
                <PromoItem
                  key={addon.id}
                  title="Promotions"
                  onClick={handleAction}
                  item={{
                    theme: 'dark',
                    tag: {
                      title: 'Challenge',
                      icon: 'priceTag',
                      iconColor: 'white',
                    },
                    title:
                      addon.description.secondaryTitle ??
                      addon.description.plan,
                    caption: addon.description.description,
                    graphicBackground: {
                      color: '#FFFFFF',
                      image: '',
                    },
                    graphicPadding: '0px',
                    graphic: (
                      <Image
                        image={{
                          url:
                            'https://images.unsplash.com/photo-1579208575657-c595a05383b7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80',
                        }}
                        width={'100%'}
                        height="300px"
                        isActive={true}
                      />
                    ),
                    actions: (
                      <Button
                        title="Claim Offer"
                        backgroundColorOverride="#cad961"
                        onClick={handleAction}
                      />
                    ),
                  }}
                />
              )
            })}
          </Flex>
        )}
        <Flex width="100%" justifyContent={'flex-start'}>
          <RoundedFooter
            isOpen={false}
            messages={discountDisclaimer}
            actionTitle={'View Comparison'}
            onClick={() => {}}
            isChartAvailable={false}
          />
        </Flex>

        <PromptingsPackageUpsale
          sponsorId={this.props.sponsorId}
          isMobile={this.props.isMobile}
        />
        {baseAddons.length > 0 && (
          <PlanAddons
            planAddons={baseAddons}
            isMobile={this.props.isMobile}
            onSelect={this.showPlanAddonBuy}
          />
        )}
        <BasicPlan basicPlan={basicPlan} onSelect={this.showPlanUpgrade} />
        <WaysToSend screenWidth={this.props.screenWidth} />
        <PlanContentsGrid />
        <AutomatedSendsGrid />
        <AccountStorage screenWidth={this.props.screenWidth} />
      </Flex>
    )
  }
}

export default connect(state => {
  return {
    screenWidth: state.window.width,
    isMobile: state.window.isMobile,
    sponsorId: state.storefront.sponsorSlug
      ? state.storefront.sponsorSlug
      : state.storefront.storefront?.user.genealogyId.toString(),
  }
}, {})(Pricing)
